import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Printer,
  Mail,
  Pencil,
  Trash2,
  X,
  Plus,
  Link2,
  ExternalLink,
  Phone,
  CreditCard,
  RotateCcw,
  Send,
  Truck,
  PackageCheck,
  CalendarClock,
  Check,
  Eraser,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { printPdf } from '@/lib/printPdf';
import { useAuthContext } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { formatCents, formatDate, todayISO } from '@/lib/utils';
import { PICKUP_STATUS_BADGE, COUNTERTOP_JOB_STATUS_BADGE } from '@/lib/badges';
import type { CountertopJobStatusUI } from '@/lib/api-types';
import AttachmentsPanel from '@/components/AttachmentsPanel';
import EmailPdfModal from '@/components/EmailPdfModal';
import RecordPickupModal from '@/components/RecordPickupModal';
import { LinkPoToEstimateModal } from '@/components/LinkPoToEstimateModal';
import type { Estimate, EstimateItem, Payment, EstimateStatus, POStatus } from '@billflow/shared';
import {
  isAreaUnit,
  describeSqftQuantity,
  boxCountLabel,
  WRITE_OFF_CAP_CENTS,
} from '@billflow/shared';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EstimateItemExtended extends EstimateItem {
  item_name: string | null;
  category: string | null;
  unit: string | null;
  dimensions: string | null;
  weight: number | null;
  pack_qty: number | null;
  pack_unit: string | null;
}

interface PaymentExtended extends Payment {
  collected_by_name?: string | null;
}

interface LinkedPO {
  id: number;
  number: string;
  status: POStatus;
  total: number;
  order_date: string;
}

type PickupStatus = 'na' | 'awaiting' | 'partial' | 'complete';

interface PickupEvent {
  id: number;
  number: string;
  date: string;
  received_by: string | null;
  notes: string | null;
  signature_path: string | null;
  photo_path: string | null;
  created_by_name: string | null;
}

interface PickupItem {
  id: number;
  pickup_id: number;
  estimate_item_id: number;
  item_id: number | null;
  description: string;
  qty: number;
  location: 'store' | 'supplier';
  unit: string | null;
  sqft_per_box: number | null;
  sqft_per_piece: number | null;
}

interface PickupLine {
  estimate_item_id: number;
  item_id: number | null;
  description: string;
  item_name: string | null;
  unit: string | null;
  sold_qty: number;
  already_picked_qty: number;
  remaining_qty: number;
  sqft_per_box: number | null;
  sqft_per_piece: number | null;
}

interface EstimateResponse {
  data: {
    estimate: Estimate & {
      customer_name?: string;
      pickup_status?: PickupStatus | null;
      expected_pickup_date?: string | null;
    };
    items: EstimateItemExtended[];
    customer: {
      id: number;
      name: string;
      contact_person: string | null;
      phone: string | null;
      email: string | null;
      address: string | null;
      credit_limit: number;
      store_credit_balance: number;
    };
    payments: PaymentExtended[];
    linked_pos: LinkedPO[];
    returns: Array<{ id: number; number: string; status: string; total: number; date: string }>;
    pickups?: PickupEvent[];
    pickup_items?: PickupItem[];
    pickup_lines?: PickupLine[];
    countertop_job?: { id: number; number: string; status: CountertopJobStatusUI } | null;
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusBadge(status: EstimateStatus) {
  switch (status) {
    case 'draft':
      return <Badge variant="gray">Draft</Badge>;
    case 'issued':
      return <Badge variant="blue">Issued</Badge>;
    case 'partial':
      return <Badge variant="amber">Partial</Badge>;
    case 'paid':
      return <Badge variant="green">Paid</Badge>;
    case 'returned':
      return <Badge variant="red">Returned</Badge>;
    default:
      return <Badge variant="gray">{status}</Badge>;
  }
}

function poBadge(status: POStatus) {
  switch (status) {
    case 'draft':
      return <Badge variant="gray">Draft</Badge>;
    case 'sent':
      return <Badge variant="blue">Sent</Badge>;
    case 'received':
      return <Badge variant="green">Received</Badge>;
    case 'returned':
      return <Badge variant="amber">Returned</Badge>;
    case 'cancelled':
      return <Badge variant="red">Cancelled</Badge>;
    default:
      return <Badge variant="gray">{status}</Badge>;
  }
}

function returnStatusBadge(status: string) {
  switch (status) {
    case 'open':
      return <Badge variant="amber">Open</Badge>;
    case 'refunded':
      return <Badge variant="green">Refunded</Badge>;
    default:
      return <Badge variant="gray">{status}</Badge>;
  }
}

// Renders the pickup status badge from the shared map (skips the 'na'/unset case).
function pickupBadge(status: PickupStatus | null | undefined) {
  if (!status || status === 'na') return null;
  const cfg = PICKUP_STATUS_BADGE[status];
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

function formatDiscount(item: EstimateItemExtended): string {
  if (!item.discount_type) return '—';
  if (item.discount_type === 'pct') return `${item.discount_value}%`;
  return formatCents(item.discount_amount);
}

// Compact number formatter for UOM/coverage displays: integers stay clean, fractions show 2dp.
const numFmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

// ─── Collect Payment Modal ─────────────────────────────────────────────────

interface CollectPaymentModalProps {
  estimateId: number;
  customerId: number;
  balanceDue: number;
  storeCreditBalance: number;
  onClose: () => void;
  onSaved: () => void;
}

function CollectPaymentModal({
  estimateId,
  customerId,
  balanceDue,
  storeCreditBalance,
  onClose,
  onSaved,
}: CollectPaymentModalProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const [amountStr, setAmountStr] = useState(String((Math.max(balanceDue, 0) / 100).toFixed(2)));
  const [creditStr, setCreditStr] = useState('0.00');
  const [date, setDate] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [writeOff, setWriteOff] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const maxCreditCents = Math.min(storeCreditBalance, Math.max(balanceDue, 0));
  const cashCents = Math.round((parseFloat(amountStr) || 0) * 100);
  const creditCents = Math.round((parseFloat(creditStr) || 0) * 100);
  // What's left after this payment posts — only admins may forgive it, and only
  // within the same small-balance cap the backend enforces.
  const projectedRemaining = balanceDue - cashCents - creditCents;
  const showWriteOff =
    isAdmin && projectedRemaining > 0 && projectedRemaining <= WRITE_OFF_CAP_CENTS;

  // If the amount changes and the row hides, don't silently carry a stale "checked" forward.
  useEffect(() => {
    if (!showWriteOff) setWriteOff(false);
  }, [showWriteOff]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (cashCents + creditCents <= 0) {
      toast.error('Enter a payment amount');
      return;
    }
    if (creditCents > storeCreditBalance) {
      toast.error('Insufficient store credit');
      return;
    }
    setLoading(true);
    try {
      await apiFetch('/api/payments', {
        method: 'POST',
        body: JSON.stringify({
          estimate_id: estimateId,
          amount: cashCents,
          store_credit_applied: creditCents > 0 ? creditCents : undefined,
          date,
          notes: notes.trim() || undefined,
          write_off_remainder: showWriteOff && writeOff ? true : undefined,
        }),
      });
      toast.success('Payment recorded');
      queryClient.invalidateQueries({ queryKey: ['customer-ledger', String(customerId)] });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Collect Payment</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pay-amount">Cash Amount ($)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="pay-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Date</Label>
              <Input
                id="pay-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          {storeCreditBalance > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="pay-credit">Apply Store Credit ($)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="pay-credit"
                  type="number"
                  min="0"
                  max={(maxCreditCents / 100).toFixed(2)}
                  step="0.01"
                  value={creditStr}
                  onChange={(e) => setCreditStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  disabled={loading}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Available:{' '}
                <span className="font-medium tabular-nums text-green-700 dark:text-green-400">
                  {formatCents(storeCreditBalance)}
                </span>
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="pay-notes">Notes (optional)</Label>
            <Input
              id="pay-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash payment"
              disabled={loading}
            />
          </div>
          {balanceDue > 0 && (
            <p className="text-xs text-muted-foreground">
              Balance due:{' '}
              <span className="font-medium tabular-nums">{formatCents(balanceDue)}</span>
            </p>
          )}
          {showWriteOff && (
            <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
              <input
                type="checkbox"
                checked={writeOff}
                onChange={(e) => setWriteOff(e.target.checked)}
                disabled={loading}
                className="h-4 w-4 rounded border-input"
              />
              Write off remaining {formatCents(projectedRemaining)} (mark Paid in full)
            </label>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Record Payment'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Issue Estimate Modal ──────────────────────────────────────────────────

interface IssueEstimateModalProps {
  estimateId: number;
  balanceDue: number;
  onClose: () => void;
  onSaved: () => void;
}

interface IssueEstimateResponse {
  data: { estimate: Estimate };
  warning?: { overage: number };
}

function IssueEstimateModal({ estimateId, balanceDue, onClose, onSaved }: IssueEstimateModalProps) {
  const [collect, setCollect] = useState(false);
  const [amountStr, setAmountStr] = useState(String((Math.max(balanceDue, 0) / 100).toFixed(2)));
  const [date, setDate] = useState(todayISO());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const cents = Math.round((parseFloat(amountStr) || 0) * 100);
    const body = collect && cents > 0 ? { payment_amount: cents, payment_date: date } : {};
    setLoading(true);
    try {
      const res = await apiFetch<IssueEstimateResponse>('/api/estimates/' + estimateId + '/issue', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      toast.success('Estimate issued');
      if (res.warning) {
        toast.warning(
          `Credit limit exceeded by ${formatCents(res.warning.overage)} — estimate was still issued.`,
        );
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Issue Estimate</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none">
            <input
              type="checkbox"
              checked={collect}
              onChange={(e) => setCollect(e.target.checked)}
              disabled={loading}
              className="h-4 w-4 rounded border-input"
            />
            Collect payment now
          </label>
          {collect ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="issue-amount">Amount ($)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                    $
                  </span>
                  <Input
                    id="issue-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={amountStr}
                    onChange={(e) => setAmountStr(e.target.value)}
                    className="pl-7 tabular-nums"
                    disabled={loading}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="issue-date">Date</Label>
                <Input
                  id="issue-date"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">This will mark the estimate as Issued.</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Issuing…' : 'Issue Estimate'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Edit Payment Modal ────────────────────────────────────────────────────

interface EditPaymentModalProps {
  payment: PaymentExtended;
  onClose: () => void;
  onSaved: () => void;
}

function EditPaymentModal({ payment, onClose, onSaved }: EditPaymentModalProps) {
  const [amountStr, setAmountStr] = useState(String((payment.amount / 100).toFixed(2)));
  const [date, setDate] = useState(payment.date.slice(0, 10));
  const [notes, setNotes] = useState(payment.notes ?? '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    const amount = Math.round((parseFloat(amountStr) || 0) * 100);
    if (amount <= 0) {
      setError('Amount must be greater than zero.');
      return;
    }
    setLoading(true);
    try {
      await apiFetch(`/api/payments/${payment.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          amount,
          date,
          notes: notes.trim() || undefined,
        }),
      });
      toast.success('Payment updated');
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">Edit Payment</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="ep-amount">
                Amount ($) <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  $
                </span>
                <Input
                  id="ep-amount"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={amountStr}
                  onChange={(e) => setAmountStr(e.target.value)}
                  className="pl-7 tabular-nums"
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ep-date">Date</Label>
              <Input
                id="ep-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ep-notes">Notes (optional)</Label>
            <Input
              id="ep-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. Cash payment"
              disabled={loading}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Confirm Delete Dialog ─────────────────────────────────────────────────

interface ConfirmDeleteProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  // Overridable for non-delete destructive/lifecycle confirmations (e.g. write-off) that reuse
  // this same dialog shell. Default to the delete copy so existing call sites are untouched.
  confirmLabel?: string;
  confirmingLabel?: string;
}

function ConfirmDeleteDialog({
  title,
  message,
  onConfirm,
  onCancel,
  loading,
  confirmLabel = 'Delete',
  confirmingLabel = 'Deleting…',
}: ConfirmDeleteProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button type="button" variant="destructive" onClick={onConfirm} disabled={loading}>
              {loading ? confirmingLabel : confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────

export default function EstimateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthContext();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Modal state ──
  const [collectPaymentOpen, setCollectPaymentOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [editPayment, setEditPayment] = useState<PaymentExtended | null>(null);
  const [deletePaymentId, setDeletePaymentId] = useState<number | null>(null);
  const [deletingPayment, setDeletingPayment] = useState(false);
  const [deleteEstimateOpen, setDeleteEstimateOpen] = useState(false);
  const [deletingEstimate, setDeletingEstimate] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writingOff, setWritingOff] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [recordPickupOpen, setRecordPickupOpen] = useState(false);
  const [linkPoOpen, setLinkPoOpen] = useState(false);
  const [deletePickupId, setDeletePickupId] = useState<number | null>(null);
  const [deletingPickup, setDeletingPickup] = useState(false);
  const [markingPickup, setMarkingPickup] = useState(false);
  // Reschedule ("schedule for later") dialog state
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  // Inline expected-pickup-date editor state
  const [editingPickupDate, setEditingPickupDate] = useState(false);
  const [pickupDateDraft, setPickupDateDraft] = useState('');
  const [savingPickupDate, setSavingPickupDate] = useState(false);
  // Per-line pickup display unit (box default, sq ft fallback), keyed by estimate_item_id.
  const [pickupUnitById, setPickupUnitById] = useState<Record<number, 'box' | 'sqft'>>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => apiFetch<EstimateResponse>(`/api/estimates/${id}`),
    enabled: !!id,
  });

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['estimate', id] });
    queryClient.invalidateQueries({ queryKey: ['estimates'] });
  }

  // ── Write off remaining balance (admin, small-balance close-out) ──
  async function handleWriteOff() {
    setWritingOff(true);
    try {
      await apiFetch(`/api/estimates/${id}/write-off`, { method: 'POST' });
      toast.success('Balance written off');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to write off balance');
    } finally {
      setWritingOff(false);
      setWriteOffOpen(false);
    }
  }

  // ── Delete estimate ──
  async function handleDeleteEstimate() {
    setDeletingEstimate(true);
    try {
      await apiFetch(`/api/estimates/${id}`, { method: 'DELETE' });
      toast.success('Estimate deleted');
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      navigate('/estimates');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete estimate');
    } finally {
      setDeletingEstimate(false);
      setDeleteEstimateOpen(false);
    }
  }

  // ── Delete payment ──
  async function handleDeletePayment(paymentId: number) {
    setDeletingPayment(true);
    try {
      await apiFetch(`/api/payments/${paymentId}`, { method: 'DELETE' });
      toast.success('Payment deleted');
      invalidate();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete payment');
    } finally {
      setDeletingPayment(false);
      setDeletePaymentId(null);
    }
  }

  // ── Delete pickup event ──
  async function handleDeletePickup(pickupId: number) {
    setDeletingPickup(true);
    try {
      await apiFetch(`/api/pickups/${pickupId}`, { method: 'DELETE' });
      toast.success('Pickup deleted');
      queryClient.invalidateQueries({ queryKey: ['estimate', id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete pickup');
    } finally {
      setDeletingPickup(false);
      setDeletePickupId(null);
    }
  }

  // ── Mark all remaining goods picked up (one-tap fast path) ──
  async function handleMarkPickedUpInFull(lines: PickupLine[]) {
    const items = lines
      .filter((l) => l.remaining_qty > 0)
      .map((l) => ({
        estimate_item_id: l.estimate_item_id,
        qty: l.remaining_qty,
        location: 'store' as const,
      }));
    if (items.length === 0) return;
    setMarkingPickup(true);
    try {
      await apiFetch('/api/pickups', {
        method: 'POST',
        body: JSON.stringify({ estimate_id: Number(id), date: todayISO(), items }),
      });
      toast.success('Marked picked up in full');
      queryClient.invalidateQueries({ queryKey: ['estimate', id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to record pickup');
    } finally {
      setMarkingPickup(false);
    }
  }

  // ── Reschedule pickup (reverse an immediate/recorded pickup back to awaiting) ──
  async function handleReschedulePickup() {
    setRescheduling(true);
    try {
      await apiFetch(`/api/estimates/${id}/reschedule-pickup`, {
        method: 'POST',
        body: JSON.stringify(rescheduleDate ? { expected_pickup_date: rescheduleDate } : {}),
      });
      toast.success('Scheduled for later');
      queryClient.invalidateQueries({ queryKey: ['estimate', id] });
      setRescheduleOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reschedule pickup');
    } finally {
      setRescheduling(false);
    }
  }

  // ── Save the expected pickup date (awaiting/partial) — null/empty clears it ──
  async function handleSavePickupDate() {
    setSavingPickupDate(true);
    try {
      await apiFetch(`/api/estimates/${id}/expected-pickup-date`, {
        method: 'PATCH',
        body: JSON.stringify({ expected_pickup_date: pickupDateDraft || null }),
      });
      toast.success('Pickup date updated');
      queryClient.invalidateQueries({ queryKey: ['estimate', id] });
      setEditingPickupDate(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update pickup date');
    } finally {
      setSavingPickupDate(false);
    }
  }

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  if (error || !data?.data.estimate) {
    return (
      <div className="p-6">
        <Link
          to="/estimates"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Estimates
        </Link>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Estimate not found.'}
        </div>
      </div>
    );
  }

  const {
    estimate,
    items,
    customer,
    payments,
    linked_pos,
    returns = [],
    pickups = [],
    pickup_items = [],
    pickup_lines = [],
    countertop_job = null,
  } = data.data;
  const balanceDue = estimate.total - estimate.amount_paid;
  const canCollectPayment = estimate.status === 'issued' || estimate.status === 'partial';
  // Estimates with sales returns OR pickups can't be edited (both reference their lines — FK safety).
  const canEdit =
    (estimate.status === 'draft' || (isAdmin && estimate.status === 'issued')) &&
    returns.length === 0 &&
    (pickups?.length ?? 0) === 0;

  // ── Pickup display state ──
  const pickupStatus = estimate.pickup_status ?? null;
  const totalRemaining = pickup_lines.reduce((sum, l) => sum + l.remaining_qty, 0);
  // Goods still to collect on an issued invoice. Keyed on remaining (not pickup_status)
  // so it also works for older estimates that predate pickup tracking, where
  // pickup_status is null until the first pickup is recorded.
  const canRecordPickup =
    totalRemaining > 0 &&
    (estimate.status === 'issued' || estimate.status === 'partial' || estimate.status === 'paid');
  // "Schedule for later" reverses a recorded pickup. Admins always; operators only when nothing
  // is signed (the server enforces this too — the gate just avoids a guaranteed-fail click).
  const hasSignedPickup = pickups.some((p) => p.signature_path);
  const canReschedule =
    (pickupStatus === 'complete' || pickupStatus === 'partial') && (isAdmin || !hasSignedPickup);
  // The expected pickup date is editable while there's still something to collect (all staff).
  const canEditPickupDate = canRecordPickup;

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* ── Back nav ── */}
      <Link
        to="/estimates"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Estimates
      </Link>

      {/* ── Body: 60/40 grid — header + line items in the 60% main column ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Main column (60%) ── */}
        <div className="lg:col-span-3 space-y-6">
          {/* ── Header card ── */}
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {/* Left: number, status, customer, date */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h1 className="text-2xl font-bold tracking-tight tabular-nums">
                      {estimate.number}
                    </h1>
                    {statusBadge(estimate.status)}
                    {pickupBadge(pickupStatus)}
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-sm">
                    <Link
                      to={`/customers/${customer.id}`}
                      className="font-medium text-foreground hover:underline inline-flex items-center gap-1"
                    >
                      {customer.name}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </Link>
                    {customer.phone && (
                      <>
                        <span className="hidden sm:inline text-muted-foreground">·</span>
                        <a
                          href={`tel:${customer.phone}`}
                          className="text-muted-foreground hover:text-foreground hover:underline inline-flex items-center gap-1"
                        >
                          <Phone className="h-3 w-3" />
                          {customer.phone}
                        </a>
                      </>
                    )}
                    <span className="hidden sm:inline text-muted-foreground">·</span>
                    <span className="text-muted-foreground">{formatDate(estimate.date)}</span>
                    {estimate.expected_pickup_date &&
                      (() => {
                        const overdue =
                          estimate.expected_pickup_date! < todayISO() &&
                          (pickupStatus === 'awaiting' || pickupStatus === 'partial');
                        return (
                          <>
                            <span className="hidden sm:inline text-muted-foreground">·</span>
                            <span
                              className={
                                overdue ? 'font-medium text-destructive' : 'text-muted-foreground'
                              }
                            >
                              Pickup by {formatDate(estimate.expected_pickup_date!)}
                              {overdue && ' · Overdue'}
                            </span>
                          </>
                        );
                      })()}
                  </div>
                  {countertop_job && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-muted-foreground">Countertop Job:</span>
                      <Link
                        to={`/countertop-jobs/${countertop_job.id}`}
                        className="font-mono font-medium text-primary hover:underline"
                      >
                        {countertop_job.number}
                      </Link>
                      {(() => {
                        const cfg = COUNTERTOP_JOB_STATUS_BADGE[countertop_job.status];
                        return cfg ? <Badge variant={cfg.variant}>{cfg.label}</Badge> : null;
                      })()}
                    </div>
                  )}
                  {estimate.notes && (
                    <p className="text-sm text-muted-foreground max-w-prose whitespace-pre-line">
                      {estimate.notes}
                    </p>
                  )}
                </div>

                {/* Right: action buttons */}
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => printPdf(`/api/estimates/${id}/pdf`)}
                  >
                    <Printer className="h-3.5 w-3.5 mr-1.5" />
                    Print
                  </Button>
                  <a
                    href={`/api/estimates/${id}/pdf?download=1`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium hover:bg-muted transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download PDF
                  </a>
                  <Button variant="outline" size="sm" onClick={() => setEmailModalOpen(true)}>
                    <Mail className="h-3.5 w-3.5 mr-1.5" />
                    Email
                  </Button>
                  {canEdit && (
                    <Link to={`/estimates/${id}/edit`}>
                      <Button variant="outline" size="sm">
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />
                        Edit
                      </Button>
                    </Link>
                  )}
                  {estimate.status === 'draft' && (
                    <Button variant="outline" size="sm" onClick={() => setIssueOpen(true)}>
                      <Send className="h-3.5 w-3.5 mr-1.5" />
                      Issue
                    </Button>
                  )}
                  {(estimate.status === 'issued' ||
                    estimate.status === 'partial' ||
                    estimate.status === 'paid') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate('/estimates/' + estimate.id + '/return')}
                    >
                      <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                      Create Return
                    </Button>
                  )}
                  {isAdmin &&
                    (estimate.status === 'issued' || estimate.status === 'partial') &&
                    balanceDue > 0 &&
                    balanceDue <= WRITE_OFF_CAP_CENTS && (
                      <Button variant="outline" size="sm" onClick={() => setWriteOffOpen(true)}>
                        <Eraser className="h-3.5 w-3.5 mr-1.5" />
                        Write off balance
                      </Button>
                    )}
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setDeleteEstimateOpen(true)}
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* ── Line items + totals ── */}
          <Card>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                Line Items
              </h2>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/20">
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground w-8">
                      #
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                      Description
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-20">
                      Qty
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Unit Price
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Discount
                    </th>
                    <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground w-28">
                      Line Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => {
                    const isArea = isAreaUnit(item.unit ?? '');
                    const boxPrice = Math.round(item.unit_price * (item.sqft_per_box ?? 0));
                    return (
                      <tr
                        key={item.id}
                        className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                      >
                        <td className="px-3 py-2.5 text-xs text-muted-foreground text-center">
                          {i + 1}
                        </td>
                        <td className="px-3 py-2.5">
                          {item.item_id != null ? (
                            <Link
                              to={`/items/${item.item_id}`}
                              className="text-sm font-medium hover:underline hover:text-primary transition-colors inline-block"
                            >
                              {item.item_name ?? item.description}
                            </Link>
                          ) : (
                            <div className="text-sm font-medium">
                              {item.item_name ?? item.description}
                            </div>
                          )}
                          {item.dimensions && (
                            <div className="text-xs text-muted-foreground">{item.dimensions}</div>
                          )}
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {isArea ? (
                              <span className="text-xs text-muted-foreground">
                                {numFmt(item.qty)} sq ft
                                {(() => {
                                  const l = boxCountLabel(item.qty, item.sqft_per_box ?? 0);
                                  return l ? ` (${l})` : '';
                                })()}
                              </span>
                            ) : (
                              <>
                                {item.pack_qty != null && item.pack_unit && (
                                  <span className="text-xs text-muted-foreground">
                                    {numFmt(item.qty)} {item.unit ?? ''} ={' '}
                                    {numFmt(item.qty * item.pack_qty)} {item.pack_unit}
                                  </span>
                                )}
                                {item.unit && (
                                  <span className="text-xs text-muted-foreground">{item.unit}</span>
                                )}
                              </>
                            )}
                          </div>
                          {item.note && (
                            <div className="text-xs italic text-muted-foreground mt-0.5">
                              {item.note}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm">{item.qty}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm">
                          {isArea ? (
                            <div className="flex flex-col items-end leading-tight">
                              <span>{formatCents(item.unit_price)} / sq.ft.</span>
                              {(item.sqft_per_box ?? 0) > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  ({formatCents(boxPrice)} / box)
                                </span>
                              )}
                            </div>
                          ) : (
                            formatCents(item.unit_price)
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm text-amber-700 dark:text-amber-400">
                          {formatDiscount(item)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                          {formatCents(item.line_total)}
                        </td>
                      </tr>
                    );
                  })}
                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-8 text-center text-sm text-muted-foreground"
                      >
                        No line items.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total weight summary */}
            {(() => {
              let totalWeight = 0;
              for (const item of items) {
                if (item.weight != null) totalWeight += item.weight * item.qty;
              }
              if (totalWeight <= 0) return null;
              const round2 = (x: number) => Math.round(x * 100) / 100;
              return (
                <div className="px-4 py-2 border-t bg-muted/10 text-xs text-muted-foreground tabular-nums">
                  Total weight: {numFmt(round2(totalWeight))} lb
                </div>
              );
            })()}

            {/* Totals block */}
            <div className="px-4 py-4 border-t bg-muted/10">
              <div className="ml-auto max-w-xs space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums">{formatCents(estimate.subtotal)}</span>
                </div>
                {estimate.line_discount_total > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Line Discounts</span>
                    <span className="tabular-nums text-amber-700 dark:text-amber-400">
                      −{formatCents(estimate.line_discount_total)}
                    </span>
                  </div>
                )}
                {estimate.invoice_discount_amount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">
                      Invoice Discount
                      {estimate.invoice_discount_type === 'pct' && (
                        <span className="ml-1 text-xs">({estimate.invoice_discount_value}%)</span>
                      )}
                    </span>
                    <span className="tabular-nums text-amber-700 dark:text-amber-400">
                      −{formatCents(estimate.invoice_discount_amount)}
                    </span>
                  </div>
                )}
                {estimate.write_off > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Adjustment</span>
                    <span className="tabular-nums text-amber-700 dark:text-amber-400">
                      −{formatCents(estimate.write_off)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t font-semibold">
                  <span>Total</span>
                  <span className="tabular-nums text-base">{formatCents(estimate.total)}</span>
                </div>
                {estimate.amount_paid > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Amount Paid</span>
                    <span className="tabular-nums text-green-700 dark:text-green-400">
                      {formatCents(estimate.amount_paid)}
                    </span>
                  </div>
                )}
                {(estimate.status === 'issued' || estimate.status === 'partial') && (
                  <div className="flex items-center justify-between text-sm font-medium pt-1 border-t">
                    <span>Balance Due</span>
                    <span
                      className={`tabular-nums ${balanceDue > 0 ? 'text-destructive' : 'text-green-700 dark:text-green-400'}`}
                    >
                      {formatCents(Math.max(balanceDue, 0))}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* ── Payments section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Payments
                </h2>
                {canCollectPayment && (
                  <Button size="sm" onClick={() => setCollectPaymentOpen(true)}>
                    <CreditCard className="h-3.5 w-3.5 mr-1.5" />
                    Collect Payment
                  </Button>
                )}
              </div>

              {payments.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No payments recorded yet.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Amount
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Store Credit
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Notes
                        </th>
                        {isAdmin && <th className="px-3 py-2.5 w-20" />}
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((pmt) => (
                        <tr
                          key={pmt.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5 tabular-nums text-sm">
                            {formatDate(pmt.date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium text-green-700 dark:text-green-400">
                            {formatCents(pmt.amount)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm text-muted-foreground">
                            {pmt.store_credit_applied > 0
                              ? formatCents(pmt.store_credit_applied)
                              : '—'}
                          </td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground">
                            {pmt.notes ?? '—'}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-2.5">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  type="button"
                                  onClick={() => setEditPayment(pmt)}
                                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                  title="Edit payment"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setDeletePaymentId(pmt.id)}
                                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  title="Delete payment"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {/* ── Right sidebar ── */}
        <aside className="lg:col-span-2 space-y-6">
          {/* ── Linked POs section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Linked Purchase Orders
                </h2>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setLinkPoOpen(true)}>
                    <Link2 className="h-3.5 w-3.5 mr-1.5" />
                    Link existing PO
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/purchase-orders/new?from_estimate=${estimate.id}`)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Create PO
                  </Button>
                </div>
              </div>

              {linked_pos.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <p className="text-sm text-muted-foreground mb-3">No purchase orders linked.</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/purchase-orders/new?from_estimate=${estimate.id}`)}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Create PO from this estimate
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          PO Number
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Status
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {linked_pos.map((po) => (
                        <tr
                          key={po.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5">
                            <Link
                              to={`/purchase-orders/${po.id}`}
                              className="font-mono text-sm font-medium text-primary hover:underline"
                            >
                              {po.number}
                            </Link>
                          </td>
                          <td className="px-3 py-2.5">{poBadge(po.status)}</td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                            {formatDate(po.order_date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                            {formatCents(po.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Sales Returns section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                  Sales Returns
                </h2>
                {(estimate.status === 'issued' ||
                  estimate.status === 'partial' ||
                  estimate.status === 'paid') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/estimates/' + estimate.id + '/return')}
                  >
                    <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                    Create Return
                  </Button>
                )}
              </div>

              {returns.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No returns against this estimate.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          SR Number
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Status
                        </th>
                        <th className="px-3 py-2.5 text-left text-xs font-medium text-muted-foreground">
                          Date
                        </th>
                        <th className="px-3 py-2.5 text-right text-xs font-medium text-muted-foreground">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {returns.map((ret) => (
                        <tr
                          key={ret.id}
                          className="border-b last:border-0 hover:bg-muted/20 transition-colors"
                        >
                          <td className="px-3 py-2.5">
                            <Link
                              to={`/returns/${ret.id}`}
                              className="font-mono text-sm font-medium text-foreground hover:underline inline-flex items-center gap-1"
                            >
                              {ret.number}
                              <ExternalLink className="h-3 w-3 text-muted-foreground" />
                            </Link>
                          </td>
                          <td className="px-3 py-2.5">{returnStatusBadge(ret.status)}</td>
                          <td className="px-3 py-2.5 text-sm text-muted-foreground tabular-nums">
                            {formatDate(ret.date)}
                          </td>
                          <td className="px-3 py-2.5 text-right tabular-nums text-sm font-medium">
                            {formatCents(ret.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Pickups section ── */}
          <Card>
            <CardContent className="p-0">
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
                <div className="flex items-center gap-2 min-w-0">
                  <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                    Pickups
                  </h2>
                  {pickupBadge(pickupStatus)}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {canReschedule && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRescheduleDate(estimate.expected_pickup_date ?? '');
                        setRescheduleOpen(true);
                      }}
                    >
                      <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
                      Schedule for later
                    </Button>
                  )}
                  {canRecordPickup && (
                    <Button size="sm" onClick={() => setRecordPickupOpen(true)}>
                      <Truck className="h-3.5 w-3.5 mr-1.5" />
                      Record Pickup
                    </Button>
                  )}
                </div>
              </div>

              {/* Editable expected pickup date (awaiting / partial) */}
              {canEditPickupDate && (
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b bg-muted/30">
                  {editingPickupDate ? (
                    <div className="flex flex-1 items-center gap-2">
                      <Label htmlFor="pickup-date" className="sr-only">
                        Expected pickup date
                      </Label>
                      <Input
                        id="pickup-date"
                        type="date"
                        value={pickupDateDraft}
                        onChange={(e) => setPickupDateDraft(e.target.value)}
                        disabled={savingPickupDate}
                        className="h-8 max-w-[11rem]"
                      />
                      <Button size="sm" onClick={handleSavePickupDate} disabled={savingPickupDate}>
                        <Check className="h-3.5 w-3.5 mr-1.5" />
                        {savingPickupDate ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingPickupDate(false)}
                        disabled={savingPickupDate}
                      >
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <span className="text-sm text-muted-foreground">
                        {estimate.expected_pickup_date
                          ? `Pickup by ${formatDate(estimate.expected_pickup_date)}`
                          : 'No pickup date set'}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setPickupDateDraft(estimate.expected_pickup_date ?? '');
                          setEditingPickupDate(true);
                        }}
                        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
                        title="Edit pickup date"
                      >
                        <Pencil className="h-3 w-3" />
                        {estimate.expected_pickup_date ? 'Edit' : 'Set date'}
                      </button>
                    </>
                  )}
                </div>
              )}

              {pickupStatus === 'na' ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Not applicable — no goods to collect.
                </div>
              ) : pickups.length === 0 && pickup_lines.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No pickups yet.
                </div>
              ) : (
                <div className="divide-y">
                  {/* Per-line summary */}
                  {pickup_lines.length > 0 && (
                    <div className="px-4 py-3 space-y-2">
                      {pickup_lines.map((l) => {
                        // The Box/ft² toggle is only meaningful for area (Sq.ft.) items;
                        // Pcs/Bag goods show a plain quantity with no unit suffix.
                        const isArea = isAreaUnit(l.unit ?? '');
                        const spb = l.sqft_per_box ?? 0;
                        const hasBox = isArea && spb > 0;
                        const unit: 'box' | 'sqft' = hasBox
                          ? (pickupUnitById[l.estimate_item_id] ?? 'box')
                          : 'sqft';
                        // Round to 2dp so float drift from (sold − picked) doesn't render
                        // a clean "3" box count as "3.00".
                        const inUnit = (area: number) =>
                          Number((unit === 'box' && spb > 0 ? area / spb : area).toFixed(2));
                        const suffix = !isArea ? '' : unit === 'box' ? ' box' : ' ft²';
                        return (
                          <div key={l.estimate_item_id} className="text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium">{l.item_name ?? l.description}</div>
                              {isArea && (
                                <div
                                  className={`flex items-center rounded-md overflow-hidden border border-input h-5 shrink-0 ${
                                    hasBox ? '' : 'opacity-50 cursor-not-allowed'
                                  }`}
                                >
                                  <button
                                    type="button"
                                    disabled={!hasBox}
                                    onClick={() =>
                                      setPickupUnitById((p) => ({
                                        ...p,
                                        [l.estimate_item_id]: 'box',
                                      }))
                                    }
                                    className={`text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1.5 ${
                                      unit === 'box'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-background text-muted-foreground hover:text-foreground'
                                    } ${hasBox ? '' : 'cursor-not-allowed'}`}
                                  >
                                    Box
                                  </button>
                                  <button
                                    type="button"
                                    disabled={!hasBox}
                                    onClick={() =>
                                      setPickupUnitById((p) => ({
                                        ...p,
                                        [l.estimate_item_id]: 'sqft',
                                      }))
                                    }
                                    className={`text-[9px] font-semibold uppercase tracking-wide transition-colors h-full px-1.5 border-l border-input ${
                                      unit === 'sqft'
                                        ? 'bg-primary text-primary-foreground'
                                        : 'bg-background text-muted-foreground hover:text-foreground'
                                    } ${hasBox ? '' : 'cursor-not-allowed'}`}
                                  >
                                    ft²
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center justify-between text-xs text-muted-foreground tabular-nums">
                              <span>
                                collected {numFmt(inUnit(l.already_picked_qty))} /{' '}
                                {numFmt(inUnit(l.sold_qty))}
                                {suffix}
                              </span>
                              <span>
                                {l.remaining_qty > 0
                                  ? `${numFmt(inUnit(l.remaining_qty))}${suffix} remaining`
                                  : 'complete'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {canRecordPickup && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-1"
                          disabled={markingPickup}
                          onClick={() => handleMarkPickedUpInFull(pickup_lines)}
                        >
                          <PackageCheck className="h-3.5 w-3.5 mr-1.5" />
                          {markingPickup ? 'Saving…' : 'Mark picked up in full'}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Pickup events */}
                  {pickups.map((pk) => {
                    const lines = pickup_items.filter((pi) => pi.pickup_id === pk.id);
                    return (
                      <div key={pk.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-mono font-medium">{pk.number}</span>
                              <span className="text-muted-foreground tabular-nums">
                                {formatDate(pk.date)}
                              </span>
                            </div>
                            {pk.received_by && (
                              <div className="text-xs text-muted-foreground">
                                Received by {pk.received_by}
                              </div>
                            )}
                          </div>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => setDeletePickupId(pk.id)}
                              className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0"
                              title="Delete pickup"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        {lines.length > 0 && (
                          <ul className="mt-1.5 space-y-0.5">
                            {lines.map((pi) => {
                              // Show the collected qty in boxes/pieces for sized tiles,
                              // raw qty otherwise (Pcs/Bag or unsized).
                              const spb = pi.sqft_per_box ?? 0;
                              const qtyDisplay =
                                isAreaUnit(pi.unit ?? '') && spb > 0
                                  ? describeSqftQuantity(pi.qty, spb, pi.sqft_per_piece)
                                  : numFmt(pi.qty);
                              return (
                                <li
                                  key={pi.id}
                                  className="text-xs text-muted-foreground tabular-nums"
                                >
                                  {qtyDisplay} × {pi.description}{' '}
                                  <span className="capitalize">
                                    ({pi.location === 'supplier' ? 'Supplier' : 'Store'})
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── Attachments & Notes ── */}
          <AttachmentsPanel module="estimate" recordId={Number(id)} />
        </aside>
      </div>

      {/* ── Modals ── */}
      {collectPaymentOpen && (
        <CollectPaymentModal
          estimateId={estimate.id}
          customerId={customer.id}
          balanceDue={balanceDue}
          storeCreditBalance={customer.store_credit_balance ?? 0}
          onClose={() => setCollectPaymentOpen(false)}
          onSaved={invalidate}
        />
      )}

      {issueOpen && (
        <IssueEstimateModal
          estimateId={estimate.id}
          balanceDue={balanceDue}
          onClose={() => setIssueOpen(false)}
          onSaved={invalidate}
        />
      )}

      {editPayment && (
        <EditPaymentModal
          payment={editPayment}
          onClose={() => setEditPayment(null)}
          onSaved={invalidate}
        />
      )}

      {deletePaymentId !== null && (
        <ConfirmDeleteDialog
          title="Delete Payment"
          message="Are you sure you want to delete this payment? This action cannot be undone."
          onConfirm={() => handleDeletePayment(deletePaymentId)}
          onCancel={() => setDeletePaymentId(null)}
          loading={deletingPayment}
        />
      )}

      {deleteEstimateOpen && (
        <ConfirmDeleteDialog
          title="Delete Estimate"
          message={`Are you sure you want to delete estimate ${estimate.number}? This will reverse any stock changes and remove associated payments.`}
          onConfirm={handleDeleteEstimate}
          onCancel={() => setDeleteEstimateOpen(false)}
          loading={deletingEstimate}
        />
      )}

      {writeOffOpen && (
        <ConfirmDeleteDialog
          title="Write off balance"
          message={`Write off remaining ${formatCents(balanceDue)} and mark this invoice Paid? This records an adjustment, not a cash payment.`}
          confirmLabel="Write Off"
          confirmingLabel="Writing off…"
          onConfirm={handleWriteOff}
          onCancel={() => setWriteOffOpen(false)}
          loading={writingOff}
        />
      )}

      {recordPickupOpen && (
        <RecordPickupModal
          estimateId={estimate.id}
          estimateNumber={estimate.number}
          onClose={() => setRecordPickupOpen(false)}
          onSaved={() => queryClient.invalidateQueries({ queryKey: ['estimate', id] })}
        />
      )}

      {linkPoOpen && (
        <LinkPoToEstimateModal
          estimateId={estimate.id}
          estimateNumber={estimate.number}
          onClose={() => setLinkPoOpen(false)}
          onLinked={() => queryClient.invalidateQueries({ queryKey: ['estimate', id] })}
        />
      )}

      {deletePickupId !== null && (
        <ConfirmDeleteDialog
          title="Delete Pickup"
          message="Are you sure you want to delete this pickup? This will reverse the recorded collection."
          onConfirm={() => handleDeletePickup(deletePickupId)}
          onCancel={() => setDeletePickupId(null)}
          loading={deletingPickup}
        />
      )}

      {rescheduleOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg border bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-base font-semibold">Schedule for later</h2>
              <button
                type="button"
                onClick={() => setRescheduleOpen(false)}
                className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-muted-foreground">
                This undoes the recorded pickup and marks the order as awaiting pickup.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="reschedule-date">Expected pickup date</Label>
                <Input
                  id="reschedule-date"
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  disabled={rescheduling}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setRescheduleOpen(false)}
                  disabled={rescheduling}
                >
                  Cancel
                </Button>
                <Button type="button" onClick={handleReschedulePickup} disabled={rescheduling}>
                  <CalendarClock className="h-3.5 w-3.5 mr-1.5" />
                  {rescheduling ? 'Scheduling…' : 'Schedule for later'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <EmailPdfModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        endpoint={`/api/estimates/${id}/email`}
        defaultTo={customer.email ?? ''}
        defaultSubject={`Estimate ${estimate.number}`}
        pdfHref={`/api/estimates/${id}/pdf`}
      />
    </div>
  );
}
